import { GetPrintBadgeRegistrationDetails, GetUnprintedRegistrationIds, IFormFile, PrintBadgeRegistrations } from '@/api/dtos';
import useGetPrintSettings from '@/hooks/useGetPrintSettings';
import usePrintAsmBadges from '@/hooks/usePrintAsmBadges';
import { faCheck, faPrint, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tabs,
    TextField,
    Tooltip,
    Typography
} from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import AdminNavigation from './AdminNavigation';

import useFileUpload, { useGetFileUpload } from '@/hooks/useFileUpload';
import useGetPrintBadgeRegistrationDetails from '@/hooks/useGetPrintBadgeRegistrationDetails';
import useGetUnprintedRegistrationIds from '@/hooks/useGetUnprintedRegistrationIds';
import useLocalStorage from '@/hooks/useLocalStorage';
import usePrintBadgeRegistration from '@/hooks/usePrintBadgeRegistration';
import {
    faDownload,
    faGear,
    faTrash,
    faUpload
} from '@fortawesome/pro-duotone-svg-icons';
import { immerable } from 'immer';
import { enqueueSnackbar } from 'notistack';
import ExtendedTableCell from '../common/ExtendedTableCell';

interface ILocalPrintSettings {
    designFilePath?: string;
    backgroundFilePath?: string;
    outputFilePath?: string;
    printer?: string;
    printApi?: string;
    cloudApi?: string;
}

type BadgeSort = 'firstName' | 'lastName' | 'email' | 'isPrinted' | 'invoiceNumber';

const AdminBadgePrinting: React.FunctionComponent = () => {
    const { printBadges, pending: isPrintingFromHook } = usePrintAsmBadges();
    
    const { getPrintBadgeRegistrationDetails, pending: isLoadingBadgeData } = useGetPrintBadgeRegistrationDetails();
    const { getUnprintedRegistrationIds } = useGetUnprintedRegistrationIds();
    const { data: printSettings } = useGetPrintSettings();

    const defaultLocalPrintSettings: ILocalPrintSettings = {
        designFilePath: 'C:\\ASM_Badge.btw',
        backgroundFilePath: '',
        outputFilePath: 'C:\\',
        printer: 'PDF',
        printApi: printSettings?.printingApi,
        cloudApi: printSettings?.bartenderCloudBaseApi
    };

    const [localPrintSettings, setLocalPrintSettings] = useLocalStorage<ILocalPrintSettings>(
        'asmBadgePrintSettings',
        () => ({ ...defaultLocalPrintSettings })
    );

    const [localPrintApi, setLocalPrintApi] = useLocalStorage<ILocalPrintSettings>(
        'printApi',
        () => ({ ...defaultLocalPrintSettings })
    );
    
    const [localCloudApi, setLocalCloudApi] = useLocalStorage<ILocalPrintSettings>(
        'cloudApi',
        () => ({ ...defaultLocalPrintSettings })
    );

    const hasInitializedFromSettings = useRef(false);

    useEffect(() => {
        if (printSettings && !hasInitializedFromSettings.current) {
            hasInitializedFromSettings.current = true;
            if (printSettings.printingApi) {
                setLocalPrintApi(c => ({ ...c, printApi: printSettings.printingApi }));
            }
            if (printSettings.bartenderCloudBaseApi) {
                setLocalCloudApi(c => ({ ...c, cloudApi: printSettings.bartenderCloudBaseApi }));
            }
        }
    }, [printSettings]);

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [selected, setSelected] = useState<number[]>([]);
    const [sort, setSort] = useState<BadgeSort>('lastName');
    const [sortDescending, setSortDescending] = useState(false);

    const [isPrinting, setIsPrinting] = useState(false);
    const [printProgress, setPrintProgress] = useState<{ done: number; total: number } | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [printErrors, setPrintErrors] = useState<Array<{timestamp: Date, message: string, registrationId?: number}>>([]);

    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [hasSettingsBeenOpened, setHasSettingsBeenOpened] = useState(false);
    const [settingsTab, setSettingsTab] = useState(0);
    const [search, setSearch] = useState<string>();


    const [tempSearch, setTempSearch] = useState<string>();
    
    const availablePrinters = [
       'PDF',
       'Color Label 500'
    ];

    const handleResetSettings = () => {
        setLocalPrintApi({ ...defaultLocalPrintSettings });
        setLocalCloudApi({ ...defaultLocalPrintSettings });
    }


    const handlePrinterChange = useCallback(
        (e: { target: { value: string } }) => setLocalPrintSettings(c => ({ ...c, printer: e.target.value })),
        [setLocalPrintSettings]
    );

    const handleOutputFilePathChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) =>
            setLocalPrintSettings(c => ({ ...c, outputFilePath: event.target.value })),
        [setLocalPrintSettings]
    );

    const handleDesignFilePathChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) =>
            setLocalPrintSettings(c => ({ ...c, designFilePath: event.target.value })),
        [setLocalPrintSettings]
    );

    const handlePrintApiChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) =>
            setLocalPrintApi(c => ({ ...c, printApi: event.target.value })),
        [setLocalPrintApi]
    );

    const handleCloudApiChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) =>
            setLocalCloudApi(c => ({ ...c, cloudApi: event.target.value })),
        [setLocalCloudApi]
    );

    const handleSettingsTabChange = useCallback((_: React.SyntheticEvent, value: number) => {
        setSettingsTab(value);
    }, []);

    const handleOpen = () => {
        setIsSettingsOpen(true);
        setHasSettingsBeenOpened(true);
    };
    const handleClose = () => {
        setIsSettingsOpen(false);
    };

    
    const onSearchChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        setTempSearch(event.target.value)
    }

    React.useEffect(() => {
        const handler = setTimeout(() => {
            setSearch(tempSearch);
            // A new search filters the whole dataset server-side; go back to page 1
            // so we don't land on a now-out-of-range page.
            setPage(0);
        }, 300);

        return () => clearTimeout(handler);
    }, [tempSearch])

    

    const [isFileModalOpen, setIsFileModalOpen] = useState(false);

    const handleFileOpen = () => setIsFileModalOpen(true);

    const [selectedFile, setSelectedFile] = useState<IFormFile | null>(null);

    const { upload, isLoading } = useFileUpload();
    const { file: currentFile, mutate: mutateFile } = useGetFileUpload();

    const handleCloseFileModal = () => {
        setIsFileModalOpen(false);
        setSelectedFile(null);
    };

    const handleSaveFile = async () => {
        if (selectedFile) {
            try {
                await upload(selectedFile);
                enqueueSnackbar('File uploaded successfully', { variant: 'success' });
                await mutateFile();
            } catch (e) {
                console.error('Upload failed', e);
                enqueueSnackbar('Upload failed', { variant: 'error' });
            }
        }
        handleCloseFileModal();
    };

    const DATA_SOURCES = [
        'FULL_NAME',
        'FIRST_NAME',
        'LAST_NAME',
        'SPECIALITY',
        'INSTITUTION',
        'EMAIL',
        'QR_CODE',
        'TITLE',
        'YEARS_ATTENDED',
        'DELEGATE_TYPE',
        'BADGE_IMAGE_TO_USE'
    ];
    const BADGE_BACKGROUND = 
    [
        'BCT_MEMBER.png',
        'NON_MEMBER.png',
        'SPONSOR.png',
        'CONSUMER.png'
    ]

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleClearFile = () => {
        setSelectedFile(null);
    };

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const {
        registrations,
        count,
        refresh: refreshRegistrations
    } = usePrintBadgeRegistration(page, rowsPerPage, search, sort, sortDescending, { revalidateOnFocus: false });

    const handleSortClick = (clickedSort: BadgeSort) => {
        // Sorting is applied server-side across the full dataset, so reset to the
        // first page whenever the sort column or direction changes.
        setPage(0);

        if (sort === clickedSort) {
            setSortDescending(!sortDescending);
            return;
        }

        setSort(clickedSort);
        setSortDescending(false);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleSelect = (registrationId: number) => {
        const selectedIndex = selected.indexOf(registrationId);
        let newSelected: number[] = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, registrationId);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
        }
        setSelected(newSelected);
    };

    const handleCancelPrint = () => {
        abortControllerRef.current?.abort();
    };

    const handlePrintAll = async () => {
        if (!hasSettingsBeenOpened) {
            handleOpen();
            return;
        }
        if (!localPrintSettings.printer) {
            enqueueSnackbar('Please select a printer before printing.', { variant: 'info' });
            return;
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;
        setIsPrinting(true);
        setPrintProgress(null);
        try {
            // When nothing is explicitly selected, "Print All" prints every unprinted
            // badge across the whole (searched) dataset — not just the current page.
            let registrationIdsToPrint: number[];
            if (selected.length > 0) {
                registrationIdsToPrint = selected;
            } else {
                const unprintedResponse = await getUnprintedRegistrationIds(
                    new GetUnprintedRegistrationIds({ search })
                );
                registrationIdsToPrint = unprintedResponse?.registrationIds ?? [];
            }

            if (registrationIdsToPrint.length === 0) {
                enqueueSnackbar('No badges left to print.', { variant: 'info' });
                return;
            }

            const printDetailsResponse = await getPrintBadgeRegistrationDetails(
                new GetPrintBadgeRegistrationDetails({
                    registrationId: registrationIdsToPrint,
                    printSettings: {
                        labelFilePath: localPrintSettings.designFilePath,
                        printToFileFolder: localPrintSettings.outputFilePath,
                        printer: localPrintSettings.printer,
                        [immerable]: false
                    }
                })
            );

            const badgesToPrint = printDetailsResponse?.badgeData ?? [];

            if (badgesToPrint.length === 0) {
                enqueueSnackbar('No badge data returned for selected registrations.', { variant: 'info' });
                return;
            }

            setPrintProgress({ done: 0, total: badgesToPrint.length });

            const response = await printBadges({
                badges: badgesToPrint,
                printSettings: {
                    labelFilePath: localPrintSettings.designFilePath,
                    printToFileFolder: localPrintSettings.outputFilePath,
                    printer: localPrintSettings.printer,
                    [immerable]: false
                },
                barTenderUrl: localPrintApi.printApi || '',
                signal: controller.signal,
                onProgress: (done, total) => setPrintProgress({ done, total })
            });

            if (response) {
                // Show success message only if there were successful prints
                if (response.count > 0) {
                    enqueueSnackbar(`Successfully printed ${response.count} badge${response.count !== 1 ? 's' : ''}.`, { variant: 'success' });
                }

                refreshRegistrations();
                setSelected([]);

                if (response.cancelled) {
                    enqueueSnackbar(`Printing cancelled after ${response.count} badge${response.count !== 1 ? 's' : ''}.`, { variant: 'info' });
                }

                // Store detailed error messages for failed badges
                if (response.errors && response.errors.length > 0) {
                    const newErrors = response.errors.map(error => ({
                        timestamp: new Date(),
                        message: error.message,
                        registrationId: error.registrationId
                    }));
                    setPrintErrors(prev => [...newErrors, ...prev]);

                    // Summary message
                    enqueueSnackbar(`${response.failedJobs} of ${response.count + response.failedJobs} badges failed to print. See error in advanced settings.`, {
                        variant: 'warning',
                        autoHideDuration: 6000
                    });
                }
            }
        } catch (error: any) {
            const errorMessage = error?.message || 'An unknown error occurred';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            abortControllerRef.current = null;
            setPrintProgress(null);
            setIsPrinting(false);
        }
    };

    const handleDownloadCurrentFile = () => {
        if (currentFile?.fileContents && currentFile?.fileName) {
            const link = document.createElement('a');
            link.href = `data:application/octet-stream;base64,${currentFile.fileContents}`;
            link.download = currentFile.fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        }
    };

    return (
        <>
            <AdminNavigation />
            <Box sx={{ paddingLeft: '100px', paddingRight: '100px', paddingTop: '30px' }}>
                <Typography variant='h2' component='h1' gutterBottom sx={{ pb: 3 }}>
                    ASM Badge Printing
                </Typography>

                <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                onChange={onSearchChange}
                                variant="outlined"
                                label="Search for name / Invoice number"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                            <Button
                                onClick={handlePrintAll}
                                disabled={isPrinting || isPrintingFromHook || isLoadingBadgeData}
                                variant='contained'
                                startIcon={
                                    isPrinting ? (
                                        <CircularProgress size={20} color='inherit' />
                                    ) : (
                                        <FontAwesomeIcon icon={faPrint} />
                                    )
                                }
                                sx={{ height: '56px', minWidth: '150px' }}
                            >
                                {isPrinting
                                    ? printProgress
                                        ? `Printing ${printProgress.done}/${printProgress.total}`
                                        : 'Preparing...'
                                    : selected.length > 0
                                    ? `Print (${selected.length})`
                                    : 'Print All'}
                            </Button>

                            {isPrinting && (
                                <Button
                                    onClick={handleCancelPrint}
                                    variant='outlined'
                                    color='error'
                                    startIcon={<FontAwesomeIcon icon={faTimes} />}
                                    sx={{ height: '56px' }}
                                >
                                    Cancel
                                </Button>
                            )}

                            <Button
                                onClick={handleOpen}
                                sx={{
                                    height: '56px',
                                    width: '56px',
                                    minWidth: '40px'
                                }}
                            >
                                <FontAwesomeIcon icon={faGear} />
                            </Button>
                            <Button
                                onClick={handleFileOpen}
                                sx={{
                                    height: '56px',
                                    width: '56px',
                                    minWidth: '40px'
                                }}
                            >
                                .btw
                            </Button>
                        </Grid>
                        <Dialog open={isFileModalOpen} onClose={handleCloseFileModal} maxWidth='sm' fullWidth>
                            <Box sx={{ p: 4 }}>
                                <Typography variant='h6' gutterBottom>
                                    Badge Design File
                                </Typography>

                                <Typography variant='subtitle2' color='text.secondary' gutterBottom sx={{ mt: 2 }}>
                                    Current File
                                </Typography>

                                {currentFile?.fileName ? (
                                    <Box
                                        sx={{
                                            mb: 2,
                                            p: 2,
                                            border: '1px solid #1976d2',
                                            borderRadius: 1,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            bgcolor: '#e3f2fd'
                                        }}
                                    >
                                        <Typography variant='body1' noWrap>
                                            {currentFile.fileName}
                                        </Typography>
                                        <Tooltip title='Download'>
                                            <IconButton
                                                onClick={handleDownloadCurrentFile}
                                                size='small'
                                                color='primary'
                                            >
                                                <FontAwesomeIcon icon={faDownload} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                ) : (
                                    <Alert severity='info' sx={{ mb: 2 }}>
                                        No file currently uploaded.
                                    </Alert>
                                )}

                                <Typography variant='subtitle2' color='text.secondary' gutterBottom sx={{ mt: 3 }}>
                                    {currentFile?.fileName ? 'Replace File' : 'Upload New File'}
                                </Typography>

                                <Button
                                    component='label'
                                    variant='outlined'
                                    fullWidth
                                    startIcon={<FontAwesomeIcon icon={faUpload} />}
                                    sx={{ mb: 2, height: '56px', borderStyle: 'dashed' }}
                                >
                                    {selectedFile ? 'Change Selected File' : 'Select .btw File'}
                                    <input type='file' accept='.btw' hidden onChange={handleFileChange} />
                                </Button>

                                {selectedFile && (
                                    <>
                                        <Box
                                            sx={{
                                                mt: 1,
                                                p: 2,
                                                border: '1px solid #4caf50',
                                                borderRadius: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                bgcolor: '#e8f5e9'
                                            }}
                                        >
                                            <Box>
                                                <Typography variant='caption' color='success.main' display='block'>
                                                    Ready to Upload:
                                                </Typography>
                                                <Typography variant='body1' noWrap>
                                                    {selectedFile.name}
                                                </Typography>
                                            </Box>
                                            <IconButton onClick={handleClearFile} size='small' color='error'>
                                                <FontAwesomeIcon icon={faTrash} />
                                            </IconButton>
                                        </Box>
                                    </>
                                )}
                                <Typography variant='caption' color='warning.main' sx={{ mt: 1, display: 'block' }}>
                                    * This is only used to view badge designs, please update the design file path in
                                    the <strong>print settings</strong> to use this file for printing.
                                </Typography>

                                <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                    <Button onClick={handleCloseFileModal} color='inherit'>
                                        Cancel
                                    </Button>
                                    <Button
                                        onClick={handleSaveFile}
                                        variant='contained'
                                        disabled={!selectedFile || isLoading}
                                    >
                                        {isLoading ? 'Uploading...' : 'Save'}
                                    </Button>
                                </Box>
                            </Box>
                        </Dialog>
                    </Grid>
                    <Typography variant='body2' sx={{ fontStyle: 'italic', mb: 1 }}>
                        * Click "Print All" to print only unprinted badges, or select specific badges to print them
                        regardless of status.
                    </Typography>

                    <Paper>
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <ExtendedTableCell
                                            sorted={sort === 'invoiceNumber'}
                                            sortDescending={sortDescending}
                                            handleClick={() => handleSortClick('invoiceNumber')}
                                        >
                                            Invoice 
                                        </ExtendedTableCell>
                                        <ExtendedTableCell
                                            sorted={sort === 'firstName'}
                                            sortDescending={sortDescending}
                                            handleClick={() => handleSortClick('firstName')}
                                        >
                                            First Name
                                        </ExtendedTableCell>
                                        <ExtendedTableCell
                                            sorted={sort === 'lastName'}
                                            sortDescending={sortDescending}
                                            handleClick={() => handleSortClick('lastName')}
                                        >
                                            Last Name
                                        </ExtendedTableCell>
                                        <ExtendedTableCell
                                            sorted={sort === 'email'}
                                            sortDescending={sortDescending}
                                            handleClick={() => handleSortClick('email')}
                                        >
                                            Email
                                        </ExtendedTableCell>
                                        <ExtendedTableCell
                                            sorted={sort === 'isPrinted'}
                                            sortDescending={sortDescending}
                                            handleClick={() => handleSortClick('isPrinted')}
                                            justify='center'
                                        >
                                            Printed
                                        </ExtendedTableCell>
                                        <TableCell align='center'>Print</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(registrations ?? []).map((row: PrintBadgeRegistrations) => (
                                        <TableRow key={row.registrationId}>
                                            <TableCell>{row.invoiceNumber}</TableCell>
                                            <TableCell>{row.firstName}</TableCell>
                                            <TableCell>{row.lastName}</TableCell>
                                            <TableCell>{row.email}</TableCell>
                                            <TableCell align='center'>
                                                {row.isPrinted ? (
                                                    <FontAwesomeIcon icon={faCheck} color='green' />
                                                ) : (
                                                    <FontAwesomeIcon icon={faTimes} color='red' />
                                                )}
                                            </TableCell>
                                            <TableCell align='center'>
                                                <Checkbox
                                                    checked={selected.indexOf(row.registrationId!) !== -1}
                                                    onChange={() => handleSelect(row.registrationId!)}
                                                ></Checkbox>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(!registrations || registrations.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={6} align='center'>
                                                No registrations found
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                       <TablePagination
                            rowsPerPageOptions={[20, 50, 100]}
                            component="div"
                            count={count ?? 0}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Paper>
                </>
                <Dialog open={isSettingsOpen} onClose={handleClose} maxWidth='lg' fullWidth>
                    <Box sx={{ p: 4 }}>
                        <Tabs
                            value={settingsTab}
                            onChange={handleSettingsTabChange}
                            sx={{ mb: 3 }}
                        >
                            <Tab label='General' />
                            <Tab label='Advanced' />
                        </Tabs>

                        <Box sx={{ minHeight: 300 }}>
                        {settingsTab === 0 && (
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl sx={{ width: '100%' }}>
                                        <InputLabel>Select Printer</InputLabel>
                                        <Select
                                            labelId='printer-select-label'
                                            id='printer-select'
                                            value={localPrintSettings.printer || ''}
                                            onChange={handlePrinterChange}
                                            label='Select Printer'
                                            variant='outlined'
                                        >
                                            {availablePrinters?.map(printer => (
                                                <MenuItem key={printer} value={printer}>
                                                    {printer}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {localPrintSettings.printer === 'PDF' && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            variant='outlined'
                                            label='File Output Path (for PDF printer)'
                                            value={localPrintSettings.outputFilePath}
                                            onChange={handleOutputFilePathChange}
                                        >
                                        </TextField>
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        variant='outlined'
                                        label='.btw Label And Background File Path'
                                        value={localPrintSettings.designFilePath}
                                        onChange={handleDesignFilePathChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant='subtitle2' gutterBottom>
                                        Allowed Data Source Names :
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {DATA_SOURCES.map(source => (
                                            <Chip
                                                key={source}
                                                label={source}
                                                size='medium'
                                                sx={{ fontFamily: 'monospace' }}
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant='subtitle2' gutterBottom>
                                        Badge Background Source Names :
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                        {BADGE_BACKGROUND.map(source => (
                                            <Chip
                                                key={source}
                                                label={source}
                                                size='medium'
                                                sx={{ fontFamily: 'monospace' }}
                                            />
                                        ))}
                                    </Box>
                                </Grid>
                            </Grid>
                        )}

                        {settingsTab === 1 && (
                            <Grid container spacing={2} sx={{ p: 2 }}>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        variant='outlined'
                                        label='Print Api URL'
                                        value={localPrintApi.printApi}
                                        onChange={handlePrintApiChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        fullWidth
                                        variant='outlined'
                                        label='BarTender Cloud Base API URL'
                                        value={localCloudApi.cloudApi}
                                        onChange={handleCloudApiChange}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
                                        Error Log
                                    </Typography>
                                    <Paper 
                                        variant="outlined" 
                                        sx={{ 
                                            maxHeight: 400, 
                                            overflow: 'auto',
                                            bgcolor: printErrors.length > 0 ? 'background.paper' : 'grey.100'
                                        }}
                                    >
                                        {printErrors.length === 0 ? (
                                            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                                                <Typography variant="body2">
                                                    No errors logged. Errors will appear here when badge printing fails.
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Table size="small">
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ fontWeight: 600, width: 160 }}>Timestamp</TableCell>
                                                        <TableCell sx={{ fontWeight: 600, width: 120 }}>RegistrationID</TableCell>
                                                        <TableCell sx={{ fontWeight: 600 }}>Error Message</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {printErrors.map((error, index) => (
                                                        <TableRow 
                                                            key={index}
                                                            sx={{ 
                                                                '&:nth-of-type(odd)': { bgcolor: 'action.hover' },
                                                                '&:hover': { bgcolor: 'action.selected' }
                                                            }}
                                                        >
                                                            <TableCell sx={{ fontSize: '0.875rem' }}>
                                                                {error.timestamp.toLocaleTimeString()}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.875rem' }}>
                                                                {error.registrationId || 'N/A'}
                                                            </TableCell>
                                                            <TableCell sx={{ fontSize: '0.875rem', wordBreak: 'break-word' }}>
                                                                {error.message}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        )}
                                    </Paper>
                                    {printErrors.length > 0 && (
                                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="secondary"
                                                onClick={() => setPrintErrors([])}
                                            >
                                                Clear Error Log
                                            </Button>
                                        </Box>
                                    )}
                                </Grid>
                                {/* Reset button */}
                                <Grid item xs={12}>
                                    <Button
                                        variant='outlined'
                                        color='secondary'
                                        onClick={handleResetSettings}
                                    >
                                        Reset Settings
                                    </Button>
                                </Grid>
                            </Grid>
                        )}
                        </Box>
                        <Button onClick={handleClose} variant='contained' sx={{ mt: 4, float: 'right' }}>
                            {hasSettingsBeenOpened ? 'Confirm' : 'Close'}
                        </Button>
                    </Box>
                </Dialog>
            </Box>
        </>
    );
};

export default AdminBadgePrinting;
